import { useState, useMemo, useCallback, useEffect } from 'react';
import { LayoutDashboard, ClipboardList, ScanBarcode } from 'lucide-react';
import BottomNav from '../../bottom-nav.jsx';
import SwipeableTabs from '../../swipeable-tabs.jsx';
import { useOnlineStore } from '../use-online-store.js';
import { useModalViewport } from '../../modal-utils.jsx';
import { F } from '../../theme.jsx';
import { fmt2 } from '../../lib/format.js';
import { OnlineStoreHeader } from './online-store-header.jsx';

import { Dashboard } from './dashboard.jsx';
import { Bestellungen } from './bestellungen.jsx';
import { Scanner } from './scanner.jsx';
import { ScannerModal, playBeep, findOrdersForCode, extractTrackingNumber } from '../scanner.jsx';
import { OrderDetailGroup } from '../order-detail.jsx';
import { OrderDetailSheet } from './order-detail-sheet.jsx';
import { LagerSheet } from './lager-sheet.jsx';
import { CatalogSheet } from './catalog-sheet.jsx';
import { StatsSheet } from './stats-sheet.jsx';
import { PreisKalkulatorSheet } from './preis-kalkulator-sheet.jsx';
import { StoreSettingsGroup } from '../../settings/store-settings.jsx';

// ── Tab definitions ───────────────────────────────────────────────────────────
// Alle Tabs — fulfillment-Rolle sieht nur Bestellungen + Scanner
const ALL_TABS = [
  { id: 'dashboard',    label: 'Übersicht',    icon: <LayoutDashboard size={22} />, ownerOnly: true },
  { id: 'bestellungen', label: 'Bestellungen', icon: <ClipboardList   size={22} /> },
  { id: 'scanner',      label: 'Scanner',      icon: <ScanBarcode     size={22} /> },
];

// ── Main container ────────────────────────────────────────────────────────────
export function OnlineStore({
  currentStore,
  currentRole,
  authToken,
  authUser,
  dark,
  setDark,
  onBack,
  onStoreDeleted,
  userStores,
}) {
  const store = useOnlineStore({
    currentStore,
    authToken,
    authUser,
    currentRole,
    dark,
    setDark,
    onBack,
    onStoreDeleted,
    userStores,
  });

  // Tabs nach helperMode filtern — Helfer sieht kein Dashboard.
  // Scanner-Tab bekommt Badge mit Anzahl Bereit-Bestellungen.
  const bereitCount = store.counts?.Bereit ?? 0;
  const TABS = useMemo(() => {
    const badge = bereitCount > 0
      ? { count: bereitCount, color: 'var(--accent-troedel)', prominent: bereitCount >= 10 }
      : undefined;
    const base = ALL_TABS.map(t => t.id === 'scanner' ? { ...t, badge } : t);
    if (store.helperMode) return base.filter(t => !t.ownerOnly);
    return base;
  }, [store.helperMode, bereitCount]);

  const [activeTab, setActiveTab] = useState(
    currentRole === 'fulfillment' ? 'bestellungen' : 'dashboard'
  );
  const [swipeProgress, setSwipeProgress] = useState(0);

  // Route-Guard: Helfer darf nicht auf Dashboard zugreifen
  useEffect(() => {
    if (store.helperMode && activeTab === 'dashboard') {
      setActiveTab('bestellungen');
    }
  }, [store.helperMode, activeTab]);
  const { height: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop } = useModalViewport();

  // Hide BottomNav when any sheet is open
  const anySheetOpen =
    store.showLager ||
    store.showCatalog ||
    store.showStats ||
    store.showCalculator ||
    store.showDetail ||
    store.showSheet ||
    store.showScanner ||
    store.showSettings ||
    store.showMembers;

  // Hoisted scanner logic (avoids IIFE anti-pattern that causes stale closures)
  const bereitOrders = useMemo(
    () => (store.orders ?? []).filter(o => o.status === 'Bereit' && !o.archived),
    [store.orders]
  );

  const handleScanResult = useCallback((code) => {
    const now = Date.now();
    const normalizedCode = typeof code === 'string' ? code.trim() : '';
    if (normalizedCode && normalizedCode === store.lastScanCodeRef?.current && now - (store.lastScanTimeRef?.current ?? 0) < 2000) return;
    if (normalizedCode && store.lastScanCodeRef) { store.lastScanCodeRef.current = normalizedCode; }
    if (normalizedCode && store.lastScanTimeRef) { store.lastScanTimeRef.current = now; }
    if (store.overlayTimerRef?.current) clearTimeout(store.overlayTimerRef.current);

    if (!normalizedCode) {
      store.setLastUnmatchedCode('Code nicht erkannt');
      store.setLastMatchedOrder(null);
      store.setScannerState('no_match');
      playBeep('error');
      if (store.overlayTimerRef) store.overlayTimerRef.current = setTimeout(() => store.setScannerState('scanning'), 2500);
      return;
    }
    const matches = findOrdersForCode(normalizedCode, store.orders ?? []);
    if (matches.length >= 1) {
      store.setLastMatchedOrder(matches[0]);
      store.setLastUnmatchedCode(null);
      store.setScannerState('match');
      playBeep('success');
      if (store.overlayTimerRef) store.overlayTimerRef.current = setTimeout(() => store.setScannerState('scanning'), 2500);
    } else {
      store.setLastUnmatchedCode(normalizedCode);
      store.setLastMatchedOrder(null);
      store.setScannerState('no_match');
      playBeep('error');
      if (store.overlayTimerRef) store.overlayTimerRef.current = setTimeout(() => store.setScannerState('scanning'), 2500);
    }
  }, [store, bereitOrders]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = useCallback(
    () => store.loadStoreData(currentStore.id, authToken, currentRole),
    [store.loadStoreData, currentStore.id, authToken, currentRole]
  );

  // Shared props passed down to all tab children
  const childProps = {
    currentRole,
    authToken,
    authUser,
    currentStore,
    dark,
    store,
  };

  return (
    <div
      className={
        'area-online-store online-store-v2' +
        (dark ? ' online-store-dark' : '')
      }
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
    >
      {/* Sticky header — respects safe-area-inset-top */}
      <OnlineStoreHeader
        title={currentStore?.name ?? 'Online Store'}
        onBack={onBack}
        onSettings={() => store.setShowSettings(true)}
        dark={dark}
        currentRole={currentRole}
      />

      {/* Swipeable tab content — Children werden aus TABS-Map aufgebaut damit
          der Index immer korrekt zur gefilterten Tab-Liste passt */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <SwipeableTabs
          tabs={TABS}
          activeTab={activeTab}
          onChange={setActiveTab}
          onSwipeProgress={setSwipeProgress}
          disabled={anySheetOpen}
        >
          {TABS.map(tab => {
            if (tab.id === 'dashboard') return (
              <Dashboard
                key="dashboard"
                {...childProps}
                onOpenLager={() => { store.setLagerProductId(null); store.setShowLager(true); }}
                onOpenStats={() => store.setShowStats(true)}
                onOpenCatalog={() => store.setShowCatalog(true)}
                onOpenCalculator={() => store.setShowCalculator(true)}
                onOpenNew={() => store.openNew?.()}
                onSwitchToScanner={() => setActiveTab('scanner')}
                onSwitchToBestellungen={() => setActiveTab('bestellungen')}
                onRefresh={handleRefresh}
              />
            );
            if (tab.id === 'bestellungen') return (
              <Bestellungen key="bestellungen" {...childProps} onRefresh={handleRefresh} />
            );
            if (tab.id === 'scanner') return (
              <Scanner key="scanner" {...childProps} />
            );
            return null;
          })}
        </SwipeableTabs>
      </div>

      {/* Bottom navigation — hidden when any sheet is open */}
      {!anySheetOpen && (
        <BottomNav
          tabs={TABS}
          activeTab={activeTab}
          onChange={setActiveTab}
          accent="online"
          dark={dark}
          swipeProgress={swipeProgress}
        />
      )}

      {/* Bottom sheets (rendered outside tab flow) */}
      <LagerSheet
        open={store.showLager}
        onOpenChange={(open) => { store.setShowLager(open); if (!open) store.setLagerProductId(null); }}
        store={store}
        currentRole={currentRole}
        dark={dark}
        loading={!store.loaded}
      />

      <CatalogSheet
        open={store.showCatalog}
        onOpenChange={(open) => store.setShowCatalog(open)}
        store={store}
        currentRole={currentRole}
        dark={dark}
        loading={!store.loaded}
      />

      <StatsSheet
        open={store.showStats}
        onOpenChange={(open) => store.setShowStats(open)}
        store={store}
        currentRole={currentRole}
        dark={dark}
        loading={!store.loaded}
      />

      <PreisKalkulatorSheet
        open={store.showCalculator}
        onOpenChange={(o) => store.setShowCalculator(o)}
        dark={dark}
      />

      <OrderDetailSheet
        open={store.showDetail}
        onOpenChange={(o) => { if (!o) store.closeDetail(); }}
        store={store}
        currentRole={currentRole}
        dark={dark}
      />

      {/* Add-order sheet (rendered by OrderDetailGroup when showSheet=true) */}
      <OrderDetailGroup
        dark={dark} t={store.t} F={F} glass={store.glass} inp={store.inp}
        appVh={appVh} appKbOpen={appKbOpen} appOffsetTop={appOffsetTop}
        isDesktop={false} desktopRightPanel={false} desktopCenterPopup={false}
        o={store.o} orders={store.orders} setOrders={store.setOrders}
        showDetail={false} closeDetail={store.closeDetail}
        showSheet={store.showSheet} closeSheet={store.closeSheet}
        form={store.form} setForm={store.setForm} save={store.save}
        inputRef={store.inputRef} isDuplicate={store.isDuplicate}
        helperMode={store.helperMode} can={store.can} currentRole={currentRole}
        getActiveChannels={store.getActiveChannels}
        getChannelLabel={store.getChannelLabel}
        getStatusLabel={store.getStatusLabel}
        getActiveBatch={store.getActiveBatch}
        channels={store.channels} products={store.products} getProductCostPreview={store.getProductCostPreview}
        changeOrderChannel={store.changeOrderChannel}
        updateOrder={store.updateOrder}
        setLabelForOrder={store.setLabelForOrder}
        openLabel={store.openLabel}
        labelUploadInputRef={store.labelUploadInputRef}
        pendingLabelOrderIdRef={store.pendingLabelOrderIdRef}
        setShipChoice={store.setShipChoice}
        setDetailDeleteConfirm={store.setDetailDeleteConfirm}
        detailDeleteConfirm={store.detailDeleteConfirm}
        deleteFromDetail={store.deleteFromDetail}
        toggleRetoureErhalten={store.toggleRetoureErhalten}
        sendungOrder={store.sendungOrder} setSendungOrder={store.setSendungOrder}
        confirmNoSendung={store.confirmNoSendung} setConfirmNoSendung={store.setConfirmNoSendung}
        trackingConfirm={store.trackingConfirm} setTrackingConfirm={store.setTrackingConfirm}
        setShowScanner={store.setShowScanner}
        apiOrderUpdate={store.apiOrderUpdate}
        confirmRetoure={store.confirmRetoure} setConfirmRetoure={store.setConfirmRetoure}
        confirmRetoureNow={store.confirmRetoureNow}
        helperSuccessBanner={store.helperSuccessBanner} setHelperSuccessBanner={store.setHelperSuccessBanner}
        confirmUnretoure={store.confirmUnretoure} setConfirmUnretoure={store.setConfirmUnretoure}
        confirmUnretoureNow={store.confirmUnretoureNow}
        manualShip={store.manualShip} setManualShip={store.setManualShip}
        shipChoice={store.shipChoice}
      />

      {/* Store Settings — channels / members / audit / delete-store */}
      <StoreSettingsGroup
        dark={dark} t={store.t} glass={store.glass}
        appVh={appVh} appKbOpen={appKbOpen} appOffsetTop={appOffsetTop}
        currentStore={currentStore} currentRole={currentRole}
        authToken={authToken} authUser={authUser}
        fmt2={fmt2} orders={store.orders} archive={store.archive}
        userStores={userStores} onBack={onBack}
        channels={store.channels}
        storeStatusLabels={store.storeStatusLabels} setStoreStatusLabels={store.setStoreStatusLabels}
        expandedChannelId={store.expandedChannelId} setExpandedChannelId={store.setExpandedChannelId}
        newChannelOpen={store.newChannelOpen} setNewChannelOpen={store.setNewChannelOpen}
        showSettings={store.showSettings} setShowSettings={store.setShowSettings}
        showChannelsConfig={store.showChannelsConfig} setShowChannelsConfig={store.setShowChannelsConfig}
        showStatusLabelsConfig={store.showStatusLabelsConfig} setShowStatusLabelsConfig={store.setShowStatusLabelsConfig}
        showMembers={store.showMembers} setShowMembers={store.setShowMembers}
        showAuditLog={store.showAuditLog} setShowAuditLog={store.setShowAuditLog}
        showCustomizeDashboard={store.showCustomizeDashboard} setShowCustomizeDashboard={store.setShowCustomizeDashboard}
        showArchive={store.showArchive} setShowArchive={store.setShowArchive}
        filterPickerOpen={store.filterPickerOpen} setFilterPickerOpen={store.setFilterPickerOpen}
        confirmDeleteStoreOpen={store.confirmDeleteStoreOpen} setConfirmDeleteStoreOpen={store.setConfirmDeleteStoreOpen}
        auditEntries={store.auditEntries} setAuditEntries={store.setAuditEntries}
        auditFilter={store.auditFilter} setAuditFilter={store.setAuditFilter}
        auditLoading={store.auditLoading} setAuditLoading={store.setAuditLoading}
        auditHasMore={store.auditHasMore} setAuditHasMore={store.setAuditHasMore}
        membersList={store.membersList} memberLoading={store.memberLoading}
        invitationsList={store.invitationsList}
        memberSheetTab={store.memberSheetTab} setMemberSheetTab={store.setMemberSheetTab}
        newInviteRole={store.newInviteRole} setNewInviteRole={store.setNewInviteRole}
        createdInviteCode={store.createdInviteCode} setCreatedInviteCode={store.setCreatedInviteCode}
        confirmRemoveUserId={store.confirmRemoveUserId} setConfirmRemoveUserId={store.setConfirmRemoveUserId}
        filterKanal={store.filterKanal} setFilterKanal={store.setFilterKanal}
        dateRange={store.dateRange} setDateRange={store.setDateRange}
        helperMode={store.helperMode}
        toggleHelperMode={store.toggleHelperMode}
        can={store.can} getAllChannels={store.getAllChannels} getChannelFinance={store.getChannelFinance}
        slugifyChannelName={store.slugifyChannelName}
        apiChannelCreate={store.apiChannelCreate} apiChannelUpdate={store.apiChannelUpdate} apiChannelDelete={store.apiChannelDelete}
        saveStoreConfig={store.saveStoreConfig}
        loadMembersAndInvitations={store.loadMembersAndInvitations}
        createInvitation={store.createInvitation} revokeInvitation={store.revokeInvitation}
        changeMemberRole={store.changeMemberRole} removeMember={store.removeMember}
        setDark={setDark}
        loadAuditLog={store.loadAuditLog} describeAuditEntry={store.describeAuditEntry}
        formatRelative={store.formatRelative}
        exportCSV={store.exportCSV} destroyCurrentStore={store.destroyCurrentStore} leaveCurrentStore={store.leaveCurrentStore}
      />

      {/* Real camera scanner modal overlay */}
      {store.showScanner && (
        <ScannerModal
          t={store.t} dark={dark} F={null}
          bereitOrders={bereitOrders}
          scannerState={store.scannerState} scannerError={store.scannerError}
          lastMatchedOrder={store.lastMatchedOrder} lastUnmatchedCode={store.lastUnmatchedCode}
          onClose={() => {
            store.setShowScanner(false);
            store.setScannerState('idle');
            store.setScannerError(null);
            store.setLastMatchedOrder(null);
            store.setLastUnmatchedCode(null);
            if (store.lastScanCodeRef) store.lastScanCodeRef.current = null;
            if (store.lastScanTimeRef) store.lastScanTimeRef.current = 0;
            if (store.overlayTimerRef?.current) clearTimeout(store.overlayTimerRef.current);
          }}
          onScanResult={handleScanResult}
          onSetState={store.setScannerState}
          onError={store.setScannerError}
          scannerControlsRef={store.scannerControlsRef}
          getStatusLabel={store.getStatusLabel}
          getChannelLabel={store.getChannelLabel}
          extractTrackingNumber={extractTrackingNumber}
        />
      )}
    </div>
  );
}
